// MMI (My MenoPath Index) chart calculations
// EXACT data from Excel file (Book2.xlsx)
// Each curve represents MMI decline for a specific menopause onset age

// Age range for X-axis
const START_AGE = 42;
const END_AGE = 54;

/**
 * Hardcoded reference curves from Excel data
 * Keys are menopause ages, values are [age, MMI] pairs from Excel
 * Using exact values from Book2.xlsx
 */
const REFERENCE_CURVES = {
  // G - Red: Menopause at age 48
  // Excel row: 100, 70, 41, 22, 10, 3.8, 1
  48: {
    42: 100,
    43: 70,
    44: 41,
    45: 22,
    46: 10,
    47: 3.8,
    48: 1,
  },
  // F - Brown: Menopause at age 49
  // Excel row: 100, 77, 48, 29, 16, 8, 3.3, 1
  49: {
    42: 100,
    43: 77,
    44: 48,
    45: 29,
    46: 16,
    47: 8,
    48: 3.3,
    49: 1,
  },
  // E - Yellow: Menopause at age 50
  // Excel row: 100, 82, 56, 38, 23, 13, 7, 3, 1
  50: {
    42: 100,
    43: 82,
    44: 56,
    45: 38,
    46: 23,
    47: 13,
    48: 7,
    49: 3,
    50: 1,
  },
  // C - Green: Menopause at age 51
  // Excel row: 100, 85, 65, 49, 35, 23, 14, 7.7, 3.5, 1
  51: {
    42: 100,
    43: 85,
    44: 65,
    45: 49,
    46: 35,
    47: 23,
    48: 14,
    49: 7.7,
    50: 3.5,
    51: 1,
  },
  // B - Blue: Menopause at age 52
  // Excel row: 100, 88, 73, 60, 47, 35, 25, 16, 9, 3.5, 1
  52: {
    42: 100,
    43: 88,
    44: 73,
    45: 60,
    46: 47,
    47: 35,
    48: 25,
    49: 16,
    50: 9,
    51: 3.5,
    52: 1,
  },
  // A - Purple: Menopause at age 53
  // Excel row: 100, 90, 80, 70, 60, 50, 40, 29, 19, 10, 4, 1
  53: {
    42: 100,
    43: 90,
    44: 80,
    45: 70,
    46: 60,
    47: 50,
    48: 40,
    49: 29,
    50: 19,
    51: 10,
    52: 4,
    53: 1,
  },
};

/**
 * Interpolate MMI value between two reference curves
 * @param {number} predictedAge - The predicted menopause age
 * @param {number} currentAge - Current age on X-axis
 * @returns {number|null} Interpolated MMI value
 */
const interpolateMMI = (predictedAge, currentAge) => {
  // Find the two closest reference curves
  const lowerAge = Math.floor(predictedAge);
  const upperAge = Math.ceil(predictedAge);
  
  // Clamp to available range (48-53)
  const clampedLower = Math.max(48, Math.min(53, lowerAge));
  const clampedUpper = Math.max(48, Math.min(53, upperAge));
  
  const lowerCurve = REFERENCE_CURVES[clampedLower];
  const upperCurve = REFERENCE_CURVES[clampedUpper];
  
  if (!lowerCurve || !upperCurve) return null;
  
  const lowerValue = lowerCurve[currentAge];
  const upperValue = upperCurve[currentAge];
  
  // If both values are undefined, no data for this age
  if (lowerValue === undefined && upperValue === undefined) return null;
  
  // Calculate the result value
  let result;
  if (lowerValue === undefined) {
    result = upperValue;
  } else if (upperValue === undefined) {
    result = lowerValue;
  } else if (clampedLower === clampedUpper) {
    result = lowerValue;
  } else {
    // Linear interpolation between curves
    const t = predictedAge - lowerAge;
    result = lowerValue + t * (upperValue - lowerValue);
  }
  
  // Stop prediction line before Y=6 - don't draw points below threshold
  if (result < 6) return null;
  
  return result;
};

/**
 * Generate reference MMI data with personalized prediction curve
 * @param {Object} estimatedMenopause - { min, max, single } predicted age
 * @returns {Array} Data points for the MMI chart
 */
export const generateMMIData = (estimatedMenopause) => {
  const referenceMMIData = [];
  
  // Calculate predicted age - use midpoint of range for interpolation between curves
  // Add small offset to show interpolation more visibly between curves
  let predictedAge = null;
  if (estimatedMenopause) {
    if (estimatedMenopause.min && estimatedMenopause.max) {
      // Use midpoint of range + 0.5 to position line visibly between curves
      // This ensures the prediction line doesn't sit exactly on a reference curve
      predictedAge = (estimatedMenopause.min + estimatedMenopause.max) / 2 + 0.5;
    } else if (estimatedMenopause.single) {
      // If only single value, add 0.5 offset to position between curves
      predictedAge = estimatedMenopause.single + 0.5;
    } else if (estimatedMenopause.min) {
      predictedAge = estimatedMenopause.min + 0.5;
    }
  }
  
  // Generate data for ages 42-54
  for (let age = START_AGE; age <= END_AGE; age++) {
    const dataPoint = { age };
    
    // Add reference curves using exact Excel data
    Object.keys(REFERENCE_CURVES).forEach(menopauseAge => {
      const curveData = REFERENCE_CURVES[menopauseAge];
      if (curveData[age] !== undefined) {
        dataPoint[`age${menopauseAge}`] = curveData[age];
      }
    });
    
    // Add dynamic user prediction based on AI-predicted menopause age
    if (predictedAge) {
      const mmiValue = interpolateMMI(predictedAge, age);
      if (mmiValue !== null) {
        dataPoint.yourPrediction = mmiValue;
      }
    }
    
    referenceMMIData.push(dataPoint);
  }
  
  return referenceMMIData;
};

/**
 * Parse estimated menopause from prediction data
 * @param {Object} predData - Prediction response from API
 * @returns {Object|null} { min, max, single } or null
 */
export const parseEstimatedMenopause = (predData) => {
  if (!predData?.predicted_age_range) return null;
  
  // Try to match a range format first (e.g., "48-52 years")
  const rangeMatch = predData.predicted_age_range.match(/(\d+)\s*[-–]\s*(\d+)/);
  if (rangeMatch) {
    return {
      min: parseInt(rangeMatch[1]),
      max: parseInt(rangeMatch[2]),
      single: null
    };
  }
  
  // Try to match a single year format (e.g., "51 years" or just "51")
  // Convert to a ±1 year range for display
  const singleMatch = predData.predicted_age_range.match(/(\d+)/);
  if (singleMatch) {
    const age = parseInt(singleMatch[1]);
    return {
      min: age - 1,
      max: age + 1,
      single: age  // Keep the original single prediction for the curve
    };
  }
  
  return null;
};
